import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import {
  ArrowLeft,
  Check2,
  ExclamationTriangleFill,
  X,
} from "react-bootstrap-icons";
import { Card, CardLabel, ColumnsRow, SectionContainer } from "../ui/RecordLayout";
import { HeaderActionButton } from "../ui/BowlerHeader";
import Spinner from "../ui/Spinner";
import {
  SpecsheetSVG,
  TAPPABLE_FIELDS,
} from "../specsheetImage/specsheetSVG.jsx";
import { ModalSpecsheets, PICKABLE_FIELDS } from "../modal/ModalSpecsheets";
import ScanReviewHeader from "./ScanReviewHeader";
import ScanValuePicker from "./ScanValuePicker";
import ScanOvalCard from "./ScanOvalCard";
import ScanBallCard from "./ScanBallCard";
import BatchDestination from "./BatchDestination";
import {
  idx_data_axisrot,
  idx_data_axistilt,
  idx_data_ball_speed,
  idx_data_ball_rev,
} from "../db/datafixtures/datafixtures";
import {
  specsheetBatchReview,
  specsheetBatchReviewClose,
  specsheetBatchConfirm,
  specsheetBatchTarget,
  specsheetHandleForm,
} from "./specsheet.redux";

/**
 * A scanned sheet, reviewed at its own address.
 *
 * The scan never writes straight into a record. The operator sees what was
 * read, the fields the model was unsure about are highlighted, and nothing is
 * applied until they say so. A wrong pitch that reaches the drill costs a
 * ball; a confirmation costs a few seconds.
 *
 * It is laid out as the bowler's screen it is about to become -- the drawing
 * on the left at 70%, the notes below it, the oval and the arsenal on the
 * right -- because it is a preview of a record, not a form about one. It used
 * to be a modal inside another modal, built from a second set of styles, and
 * read like a different program bolted onto this one.
 *
 * Two ways in, and the difference is only where the sheet lands:
 *   /scan/:jobId                    from the queue; creates a bowler and sheet
 *   /bowler/:bowlerID/scan/:jobId   from a bowler's own sheet; fills that form
 */

// Added by ModalSpecsheets for its own arithmetic and not columns on a sheet.
// They must not ride along into what gets applied.
const DERIVED_KEYS = ["thm_oval_difference", "f2_span_full_difference"];

// Shown on the drawing, in the header, in the oval card or in the arsenal
// card. Anything else the reader found has to be listed, or it would be
// applied without ever having been seen.
const HOUSED = [
  "name",
  "hand",
  "note",
  "thm_type",
  "thm_oval_starting_bit",
  "thm_oval_width",
  "thm_oval_degree",
  "thm_oval_taper",
  "thm_round_taper",
];

/**
 * The bowler screen's 70/30, on a page that scrolls.
 *
 * There, the row is a fixed-height pane and each column scrolls inside
 * itself. Here the whole review scrolls as one document -- the drawing, the
 * notes and the cards are read top to bottom -- so the inner overflow is
 * turned off. Proportions and cards are the shared ones; only the scrolling
 * differs, because the two screens are shaped differently.
 */
const Columns = styled(ColumnsRow)`
  @media only screen and (min-width: 992px) and (orientation: landscape) {
    flex: 0 0 auto;
    overflow: visible;
  }
`;

const MainColumn = styled(SectionContainer)`
  @media only screen and (min-width: 992px) and (orientation: landscape) {
    overflow: visible;
  }
`;

const SideColumn = styled(SectionContainer)`
  @media only screen and (min-width: 992px) and (orientation: landscape) {
    overflow-y: visible;
  }
`;

const Screen = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px 0;
`;

const Back = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  /* Touch-first: tapped on a tablet at a shop counter. */
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  background: transparent;
  color: #00187e;
  border-radius: 8px;

  &:hover,
  &:focus {
    background: #eef0f8;
  }
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
`;

const Banner = styled.div`
  margin: 12px 16px 0;
  background: #fef5e1;
  border: 1px solid #f5a524;
  border-radius: 10px;
  padding: 9px 14px;
  font-size: 12.5px;
  color: #7a5200;

  strong {
    color: #5c3d00;
  }
`;

const Problem = styled(Banner)`
  background: #fdecec;
  border-color: #d62828;
  color: #8f1f1f;
`;

const Segments = styled.div`
  display: inline-flex;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const Segment = styled.button`
  padding: 4px 12px;
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 36px;
  border: none;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  background: ${(p) => (p.$on ? "#00187e" : "#ffffff")};
  color: ${(p) => (p.$on ? "#ffffff" : "#6b7280")};
`;

const Compare = styled.div`
  display: grid;
  grid-template-columns: ${(p) => (p.$both ? "1fr 1fr" : "1fr")};
  gap: 12px;
  align-items: start;

  /* Stacked on anything narrow: two half-width sheets are legible on neither. */
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
`;

const Paper = styled.img`
  width: 100%;
  height: auto;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  /* Tap for full size: at half a column a handwritten 5/8 is a guess. */
  cursor: zoom-in;
`;

const Drawing = styled.div`
  font-size: 10px;
  position: relative;
  min-width: 0;

  > svg.specsheet {
    width: 100%;
    height: auto;
  }
`;

const NotesRow = styled.div`
  display: flex;
  gap: 14px;
  align-items: stretch;

  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const NoteBox = styled(Form.Control)`
  flex: 1 1 auto;
  min-height: 86px;
  font-size: 13px;
`;

const Attach = styled.div`
  flex: 0 0 132px;
`;

const AttachTile = styled.button`
  width: 100%;
  height: 86px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  background: #f6f7fa center/cover no-repeat;
  background-image: ${(p) => (p.$src ? `url(${p.$src})` : "none")};
`;

const AttachNote = styled.div`
  font-size: 9.5px;
  font-weight: 600;
  color: #16a34a;
  letter-spacing: 0.03em;
  margin-top: 5px;
`;

const CheckRow = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 40px;
  padding: 6px 8px;
  border: none;
  border-left: 3px solid ${(p) => (p.$flagged ? "#f5a524" : "transparent")};
  border-radius: 6px;
  background: ${(p) => (p.$flagged ? "#fef5e1" : "transparent")};
  font-size: 11.5px;
  text-align: left;

  & + & {
    margin-top: 3px;
  }

  &:hover {
    background: ${(p) => (p.$flagged ? "#fdecc8" : "#f5f6fa")};
  }
`;

/* A flagged row carries two decisions, so it cannot be one button: changing
   the value, and saying the value was right all along. The wrapper holds the
   stripe and the tint; the parts inside are what gets tapped. Nesting a
   button inside a button is invalid markup and the inner one stops working
   on some browsers, which is why this is not simply CheckRow with an icon. */
const CheckRowSplit = styled.div`
  display: flex;
  align-items: stretch;
  gap: 4px;
  border-left: 3px solid ${(p) => (p.$flagged ? "#f5a524" : "transparent")};
  border-radius: 6px;
  background: ${(p) => (p.$flagged ? "#fef5e1" : "transparent")};

  & + & {
    margin-top: 3px;
  }
`;

const CheckRowMain = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 40px;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  font-size: 11.5px;
  text-align: left;

  &:hover {
    background: rgba(0, 0, 0, 0.04);
  }
`;

const Accept = styled.button`
  flex: 0 0 auto;
  /* Touch-first: a 40px target, not an icon someone has to aim at. */
  width: 40px;
  min-height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  background: ${(p) => (p.$on ? "#e8f5ee" : "transparent")};
  color: ${(p) => (p.$on ? "#16803c" : "#c0a46a")};

  &:hover,
  &:focus {
    background: ${(p) => (p.$on ? "#d7eee1" : "#f6d9a0")};
    color: #16803c;
  }
`;

const Discard = styled(Accept)`
  background: ${(p) => (p.$on ? "#fdecec" : "transparent")};
  color: ${(p) => (p.$on ? "#b02020" : "#c0a46a")};

  &:hover,
  &:focus {
    background: ${(p) => (p.$on ? "#f8dada" : "#f6d9a0")};
    color: #b02020;
  }

  &:disabled {
    opacity: 0.35;
  }
`;

const RowKey = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  color: #3d4149;
  min-width: 0;
`;

const RowName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Mark asked for a Status column (#912). The side panel is ~300px and
   already carries the name, the value, a tick and a cross, so a real column
   would squeeze the value until it was cut off -- which is the one thing on
   the row that has to stay readable. It reads as a tag under the name
   instead, agreed with him. */
const STATUS_TONE = {
  pending: { bg: "#fef5e1", fg: "#9a6100" },
  edited: { bg: "#e8f5ee", fg: "#16803c" },
  accepted: { bg: "#e8f5ee", fg: "#16803c" },
  discarded: { bg: "#f1f2f6", fg: "#6b7280" },
};

const StatusTag = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 1px 6px;
  border-radius: 999px;
  white-space: nowrap;
  background: ${(p) => (STATUS_TONE[p.$status] || STATUS_TONE.pending).bg};
  color: ${(p) => (STATUS_TONE[p.$status] || STATUS_TONE.pending).fg};
`;

const RowValue = styled.span`
  font-weight: 700;
  color: ${(p) => (p.$struck ? "#9aa0ad" : "#00187e")};
  text-decoration: ${(p) => (p.$struck ? "line-through" : "none")};
  flex: 0 0 auto;
`;

const RowBlank = styled(RowValue)`
  color: #b0851f;
  font-weight: 500;
  font-style: italic;
`;

const FreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
`;

const FreeLabel = styled.span`
  flex: 1 1 45%;
  min-width: 0;
  font-size: 11.5px;
  color: #3d4149;
`;

const FreeInput = styled(Form.Control)`
  flex: 1 1 55%;
  /* Touch-first: tapped on a tablet at a shop counter. */
  min-height: 40px;
  font-size: 12.5px;
`;

/**
 * The support bubble is fixed at right 16px, 56px wide, and in landscape it
 * sits 16px off the bottom -- straight on top of this bar, which is where
 * Create bowler lives. It has z-index 2000 against this bar's 2, so it wins.
 * The room is reserved here rather than moving the bubble: it is deliberately
 * in that corner everywhere else in the app.
 */
const Foot = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  z-index: 2;

  @media (orientation: landscape) {
    padding-right: calc(88px + env(safe-area-inset-right, 0px));
  }
`;

const Muted = styled.div`
  font-size: 11.5px;
  color: #6b7280;
  margin-top: 10px;
`;

const Loading = styled.div`
  padding: 60px 0;
`;

// Reuse the labels the form already uses wherever one exists; the rest fall
// back to a humanised field name so a new column never renders as blank.
const LABEL_KEYS = {
  name: "form.label.specsheet_name",
  hand: "form.label.specsheet_hand",
  ball_grip: "form.label.bowler_grip_type",
  bridge: "form.label.bridge",
  clt: "form.label.clt",
  note: "form.label.notes",
  f1_grip_type: "form.label.insert_type",
  f1_size: "form.label.insert_size",
  f1_od: "form.label.insert_OD",
  f2_grip_type: "form.label.insert_type",
  f2_size: "form.label.insert_size",
  f2_od: "form.label.insert_OD",
  f1_rev: "form.label.reverse",
  f2_rev: "form.label.reverse",
  thm_rev: "form.label.reverse",
  f1_lat: "form.label.lateral",
  f2_lat: "form.label.lateral",
  f1_for: "form.label.forward",
  f2_for: "form.label.forward",
  thm_for: "form.label.forward",
  f1_span_full: "form.label.middle_finger_full",
  f1_span_cut: "form.label.middle_finger_cut",
  f1_span_outer2_cut: "form.label.middle_finger_outer_to_cut",
  f1_span_hole_to_hole: "form.label.middle_finger_hole_to_hole",
  f2_span_full: "form.label.ring_finger_full",
  f2_span_cut: "form.label.ring_finger_cut",
  f2_span_outer2_cut: "form.label.ring_finger_outer_to_cut",
  f2_span_hole_to_hole: "form.label.ring_finger_hole_to_hole",
  thm_insert_type: "form.label.insert_type",
  thm_size: "form.label.size",
  thm_od: "form.label.insert_OD",
  thm_flex: "form.label.flexibility",
};

const humanise = (field) =>
  field
    .replace(/^f1_/, "Middle ")
    .replace(/^f2_/, "Ring ")
    .replace(/^thm_/, "Thumb ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const optionsOf = (source, prop) => {
  const values = source.map((row) => row[prop]);
  return values.filter((v, i) => values.indexOf(v) === i);
};

// The bowler's own measurements, which are not spec sheet fields and so have
// no picker in ModalSpecsheets. Same vocabularies the bowler form uses, and
// the same ones the reader is held to server-side.
const BOWLER_PICKERS = {
  style: { options: ["", "traditional", "two_handed"], label: "specsheetScan.bowler.style" },
  axis_rot: {
    options: optionsOf(idx_data_axisrot, "axis_rot"),
    label: "form.label.axis_rotation",
  },
  axis_tilt: {
    options: optionsOf(idx_data_axistilt, "tilt_deg"),
    label: "form.label.tilt",
  },
  ball_speed: {
    options: optionsOf(idx_data_ball_speed, "speed"),
    label: "form.label.speed",
  },
  rev_rate: {
    options: optionsOf(idx_data_ball_rev, "rev"),
    label: "form.label.rev_rate",
  },
};

const ScanReviewScreen = ({
  review,
  loadingJobId,
  confirming,
  applied = {},
  candidates = [],
  created = [],
  searching,
  target,
  exactId,
  error,
  onLoad,
  onLeave,
  onConfirm,
  onTarget,
  onApplyToSheet,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { jobId, bowlerID } = useParams();

  // Filling a bowler's open sheet rather than creating one. The destination is
  // already decided, so nothing about a bowler is offered or written.
  const intoOpenSheet = !!bowlerID;
  const backTo = intoOpenSheet ? `/bowler/${bowlerID}` : "/scan";

  const [edited, setEdited] = useState({});
  const [ballEdits, setBallEdits] = useState({});
  const [bowlerEdits, setBowlerEdits] = useState({});
  const [addBall, setAddBall] = useState(true);
  // The box last tapped on the drawing. It highlights there and drives the
  // focus into the matching row.
  const [activeField, setActiveField] = useState("");
  // Which field the sheet's own picker is editing. Kept apart from
  // activeField because a row input sets that one on focus, and focusing a
  // row must not fling a picker open.
  const [pickerField, setPickerField] = useState("");
  const [bowlerField, setBowlerField] = useState("");
  const [showDestination, setShowDestination] = useState(false);
  const [view, setView] = useState("both");
  const [zoomed, setZoomed] = useState(false);
  const inputRefs = useRef({});

  useEffect(() => {
    onLoad(jobId);
    return () => onLeave();
  }, [jobId, onLoad, onLeave]);

  const reviewJobId = review ? review.jobId : "";

  // Re-seeded per job: a correction made on one sheet must not carry into the
  // next one.
  useEffect(() => {
    if (!review) return;
    setEdited({ ...review.values });
    setBallEdits(review.ball || {});
    setBowlerEdits(review.bowler || {});
    setAddBall(true);
    setActiveField("");
    setPickerField("");
    // Same reason as the values above: "I checked that one" belongs to the
    // sheet it was said about, and carrying it to the next one would clear
    // flags nobody has looked at.
    setAccepted(new Set());
    setDiscarded(new Set());
  }, [reviewJobId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Confirmed and gone. Back to where this was opened from rather than to the
  // record just created: someone working down forty rows cannot be sent to a
  // bowler's screen and back for each one.
  useEffect(() => {
    if (jobId && applied[jobId]) navigate(backTo, { replace: true });
  }, [jobId, applied, backTo, navigate]);

  useEffect(() => {
    if (!activeField) return;
    // The picker owns the field while it is open; stealing focus into the row
    // behind it would fight the operator for the keyboard.
    if (pickerField) return;
    const id = setTimeout(() => {
      const input = inputRefs.current[activeField];
      if (!input) return;
      input.scrollIntoView({ block: "center", behavior: "smooth" });
      input.focus();
      if (input.select) input.select();
    }, 0);
    return () => clearTimeout(id);
  }, [activeField, edited, pickerField]);

  // Flags the operator has settled, by saying the reader got it right. A flag
  // means "the model was not sure", which is a question, not a verdict -- and
  // a question that cannot be answered is just a stain on the screen. Without
  // this the count sat at 13 however much checking was done, so there was no
  // way to tell a value that had been looked at from one that had not.
  //
  // Held for this sitting only. Re-opening the job asks the server again and
  // the flags come back; persisting them would be a write per tap, and a
  // review is one sitting at a counter.
  const [accepted, setAccepted] = useState(() => new Set());

  // Readings the operator has thrown away. Agreed with Mark (#912): this
  // means the reading is not written, NOT that the box is emptied. The
  // difference only shows on a sheet that already has a value -- one a
  // driller measured by hand -- and a bad scan is no reason to delete it.
  // So the field is simply left out of what gets applied, which is what
  // blanking one already did.
  const [discarded, setDiscarded] = useState(() => new Set());

  // Still flagged: what the reader doubted, minus what the operator has
  // settled. Changing the value settles it too, and on its own -- the number
  // on screen is then the operator's, not the model's guess, so there is
  // nothing left to check. Only an untouched value needs the tick.
  const flagged = useMemo(() => {
    const raw = (review && review.needsReview) || [];
    const asRead = (review && review.values) || {};
    return new Set(
      raw.filter((field) => {
        if (accepted.has(field) || discarded.has(field)) return false;
        const now = edited[field];
        if (now === undefined) return true;
        return String(now ?? "") === String(asRead[field] ?? "");
      }),
    );
  }, [review, edited, accepted, discarded]);

  /**
   * What each checked field is doing, in a word.
   *
   * The states existed already -- they were only ever said in colour, and
   * "changed it myself" looked the same as "agreed with the reader". Mark
   * asked for them spelled out, which is also the only way an operator
   * working down thirteen rows can see where they got to.
   */
  const statusOf = (field) => {
    if (discarded.has(field)) return "discarded";
    if (accepted.has(field)) return "accepted";
    return flagged.has(field) ? "pending" : "edited";
  };

  // What will actually be written. A discarded field is absent rather than
  // empty, so the sheet it lands on keeps whatever it already had.
  const kept = useMemo(
    () =>
      Object.keys(edited).reduce((acc, key) => {
        if (discarded.has(key)) return acc;
        acc[key] = edited[key];
        return acc;
      }, {}),
    [edited, discarded],
  );

  const label = (field) =>
    LABEL_KEYS[field] ? t(LABEL_KEYS[field]) : humanise(field);

  // Bowlers created earlier in this same batch are offered alongside the ones
  // the search found: without them, two sheets for the same new person would
  // make two bowlers, which is the very thing this screen is here to stop.
  const offered = useMemo(() => {
    const extra = created.filter((c) => !candidates.some((x) => x.id === c.id));
    return [...candidates, ...extra];
  }, [candidates, created]);

  const chosen = offered.find((c) => c.id === target);
  const chosenName = chosen
    ? `${chosen.firstname} ${chosen.lastname}`.trim()
    : "";

  const openPicker = (field) => {
    if (BOWLER_PICKERS[field]) {
      setBowlerField(field);
      return;
    }
    setActiveField(field);
    if (PICKABLE_FIELDS.includes(field)) {
      setPickerField(field);
      // A box the reader left empty has no value yet. Give it one rather than
      // swallowing the tap: a value it missed is exactly what needs filling in.
      setEdited((current) =>
        Object.prototype.hasOwnProperty.call(current, field)
          ? current
          : { ...current, [field]: "" },
      );
    }
  };

  // What the picker decided, minus its working-out. It may set more than the
  // one field that was tapped -- choosing a grip type fills the insert size,
  // and that cascade is most of why this picker is worth reusing.
  const handlePickerCommit = (specsheetValues) => {
    // Choosing a value for a field that had been thrown away puts it back:
    // the operator has just decided it belongs on the sheet after all.
    setDiscarded((current) => {
      const next = new Set(current);
      Object.keys(specsheetValues || {}).forEach((key) => next.delete(key));
      return next;
    });
    setEdited((current) => {
      const merged = { ...current, ...specsheetValues };
      DERIVED_KEYS.forEach((key) => delete merged[key]);
      return merged;
    });
  };

  const values = review ? review.values : {};
  const previewUrl = review ? review.previewUrl : "";

  // Everything read that the drawing, the header, the oval card and the
  // arsenal card between them do not show.
  const homeless = Object.keys(edited)
    .filter((field) => !TAPPABLE_FIELDS.includes(field))
    .filter((field) => HOUSED.indexOf(field) < 0)
    .sort();

  // The checking list: everything the reader was unsure about, settled or
  // not. Settled rows stay on the list rather than vanishing -- a tap that
  // makes a row disappear with no way back is a trap on a touch screen, and
  // seeing eleven of thirteen done is the point of a worklist. The count
  // beside the title is what is left.
  const toCheck = useMemo(
    () => Array.from(new Set((review && review.needsReview) || [])).sort(),
    [review],
  );

  const keptCount = Object.keys(kept).filter(
    (k) => String(kept[k] || "").trim() !== "",
  ).length;

  const handleApply = () => {
    // Anything blanked out is dropped rather than written as an empty string,
    // so clearing a value leaves the existing one alone.
    // `kept` is `edited` without the readings thrown away. A discarded
    // field is absent rather than empty, which is the same thing to the
    // writer below and leaves an existing sheet's own value untouched.
    const applyValues = Object.keys(kept).reduce((acc, key) => {
      const value = String(kept[key] || "").trim();
      if (value !== "") acc[key] = value;
      return acc;
    }, {});

    if (intoOpenSheet) {
      onApplyToSheet(applyValues);
      navigate(backTo, { replace: true });
      return;
    }

    onConfirm({
      jobId,
      values: applyValues,
      // Whatever the operator picked, not what the name happened to match.
      customerId: target || null,
      // Only used when a bowler is being created: it seeds their phone and
      // email so the next sheet for this person is found rather than
      // duplicated.
      contact: (review && review.contact) || {},
      // Edited values win, but the gate stays the same: no model name, no
      // ball.
      ball: addBall && ballEdits && ballEdits.ball_name ? ballEdits : null,
      // Only used when a bowler is being created; the epic ignores it when
      // the sheet is going to somebody who already exists.
      bowler: bowlerEdits || {},
      // Whether there is a scanned page to keep at all. The review only
      // promises one when it has an image to show.
      hasPage: !!previewUrl,
    });
  };

  // Also guards against the previous sheet's values: batchReview still holds
  // whatever was reviewed last until this job's own read lands, and rendering
  // that for a moment would show one sheet under another one's address.
  const ready = review && review.jobId === jobId && loadingJobId !== jobId;

  if (!ready) {
    return (
      <Screen>
        <TopBar>
          <Back type="button" onClick={() => navigate(backTo)}>
            <ArrowLeft size={20} />
          </Back>
          <Crumb>{t("specsheetScan.title")}</Crumb>
        </TopBar>
        {error ? <Problem>{error}</Problem> : <Loading><Spinner /></Loading>}
      </Screen>
    );
  }

  const showPaper = previewUrl && view !== "sheet";
  const showDrawing = view !== "page";

  return (
    <>
      {/* The sheet's own picker, driven by this screen's field rather than by
          the store, so it edits the proposal in hand and never a bowler's
          live sheet. */}
      <ModalSpecsheets
        openDropdown={pickerField}
        values={edited}
        bowler={bowlerEdits}
        onCommit={handlePickerCommit}
        onCommitBowler={setBowlerEdits}
        onClose={() => setPickerField("")}
      />

      <ScanValuePicker
        show={!!bowlerField}
        title={bowlerField ? t(BOWLER_PICKERS[bowlerField].label) : ""}
        options={bowlerField ? BOWLER_PICKERS[bowlerField].options : []}
        value={bowlerField ? bowlerEdits[bowlerField] || "" : ""}
        blankLabel={t("specsheetScan.needs.blank")}
        onPick={(value) =>
          setBowlerEdits((current) => ({ ...current, [bowlerField]: value }))
        }
        onClose={() => setBowlerField("")}
      />

      <Modal
        show={showDestination}
        onHide={() => setShowDestination(false)}
        centered
      >
        <Modal.Header closeButton>
          <CardLabel>{t("specsheetScan.batch.changeDestination")}</CardLabel>
        </Modal.Header>
        <Modal.Body>
          <BatchDestination
            scannedName={values.name}
            scannedPhone={(review.contact || {}).phone}
            candidates={offered}
            searching={searching}
            exactId={exactId}
            value={target}
            onChange={(customerId) => {
              onTarget(customerId);
              setShowDestination(false);
            }}
          />
        </Modal.Body>
      </Modal>

      <Modal
        show={zoomed}
        onHide={() => setZoomed(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton />
        <Modal.Body>
          <img
            src={previewUrl}
            alt={t("specsheetScan.scannedPage")}
            style={{ width: "100%", height: "auto" }}
          />
        </Modal.Body>
      </Modal>

      <Screen>
        <TopBar>
          <Back
            type="button"
            onClick={() => navigate(backTo)}
            aria-label={t("specsheetScan.batch.backToBowlers")}
          >
            <ArrowLeft size={20} />
          </Back>
          <Crumb>{t("specsheetScan.title")}</Crumb>
        </TopBar>

        <ScanReviewHeader
          name={values.name}
          onPickName={() => openPicker("name")}
          contact={review.contact || {}}
          hand={edited.hand}
          bowler={bowlerEdits}
          targetName={chosenName}
          isExisting={!!chosen}
          onChangeDestination={
            intoOpenSheet ? null : () => setShowDestination(true)
          }
          onPick={openPicker}
          // The bowler's numbers are only ever written on a bowler being
          // created here, so against an existing record they are shown and
          // not offered.
          readOnly={intoOpenSheet || !!chosen}
        />

        {error ? <Problem>{error}</Problem> : null}

        {flagged.size > 0 && (
          <Banner>
            <strong>
              {t("specsheetScan.needsReviewCount", { count: flagged.size })}
            </strong>{" "}
            {t("specsheetScan.needsReviewHelp")}
          </Banner>
        )}

        <Columns>
          <MainColumn specsheet>
            <Card>
              <CardLabel>
                <span>{t("specsheetScan.whatWasRead")}</span>
                {previewUrl ? (
                  <Segments>
                    {["sheet", "page", "both"].map((mode) => (
                      <Segment
                        key={mode}
                        type="button"
                        $on={view === mode}
                        onClick={() => setView(mode)}
                      >
                        {t(`specsheetScan.view.${mode}`)}
                      </Segment>
                    ))}
                  </Segments>
                ) : null}
              </CardLabel>
              <Compare $both={showPaper && showDrawing}>
                {showPaper ? (
                  <Paper
                    src={previewUrl}
                    alt={t("specsheetScan.scannedPage")}
                    onClick={() => setZoomed(true)}
                  />
                ) : null}
                {showDrawing ? (
                  <Drawing>
                    <SpecsheetSVG
                      // What will be written, not everything that was read:
                      // a discarded box shows empty, because that is what
                      // the sheet is going to get.
                      formValues={kept}
                      isReadOnly
                      // The live set, not the whole list. toCheck keeps the
                      // settled rows so they can be undone, so passing it
                      // here left the drawing yellow after a row had been
                      // dealt with -- the two disagreeing on one screen.
                      flaggedFields={Array.from(flagged)}
                      onFieldClick={openPicker}
                      // Two-state toggles on the drawing have no picker to
                      // open, so they hand back the value they chose.
                      onFieldValueChange={(field, value) => {
                        setActiveField(field);
                        setEdited((current) => ({ ...current, [field]: value }));
                      }}
                      activeField={activeField}
                    />
                  </Drawing>
                ) : null}
              </Compare>
            </Card>

            <Card>
              <CardLabel>{t("specsheetScan.notesAndAttachments")}</CardLabel>
              <NotesRow>
                <NoteBox
                  as="textarea"
                  rows={3}
                  value={edited.note || ""}
                  onChange={(e) =>
                    setEdited({ ...edited, note: e.target.value })
                  }
                />
                {previewUrl ? (
                  <Attach>
                    <AttachTile
                      type="button"
                      $src={previewUrl}
                      onClick={() => setZoomed(true)}
                      aria-label={t("specsheetScan.scannedPage")}
                    />
                    {/* Said before it happens rather than discovered
                        afterwards: the page stops being working material and
                        becomes part of the record. */}
                    {!intoOpenSheet && (
                      <AttachNote>{t("specsheetScan.pageKept")}</AttachNote>
                    )}
                  </Attach>
                ) : null}
              </NotesRow>
            </Card>
          </MainColumn>

          <SideColumn right>
            <Card>
              <CardLabel>
                <span>{t("specsheetScan.needs.title")}</span>
                {flagged.size > 0 ? (
                  <span style={{ color: "#f5a524" }}>{flagged.size}</span>
                ) : null}
              </CardLabel>
              {toCheck.length === 0 ? (
                <Muted>{t("specsheetScan.needs.none")}</Muted>
              ) : (
                toCheck.map((field) => {
                  const status = statusOf(field);
                  const pending = status === "pending";
                  const agreed = status === "accepted";
                  const thrown = status === "discarded";
                  // Nothing to agree with or throw away on a box the reader
                  // left empty; that one is settled by filling it in.
                  const hasReading = Boolean(edited[field]);
                  return (
                    <CheckRowSplit key={field} $flagged={pending}>
                      <CheckRowMain
                        type="button"
                        onClick={() => openPicker(field)}
                      >
                        <RowKey>
                          <RowName>
                            {pending ? (
                              <ExclamationTriangleFill
                                size={11}
                                style={{ marginRight: 5, color: "#f5a524" }}
                              />
                            ) : null}
                            {label(field)}
                          </RowName>
                          {/* The state in a word, under the name. The panel
                              is too narrow for a column of its own without
                              squeezing the value until it is cut off. */}
                          <StatusTag $status={status}>
                            {t(`specsheetScan.needs.status.${status}`)}
                          </StatusTag>
                        </RowKey>
                        {hasReading ? (
                          <RowValue $struck={thrown}>{edited[field]}</RowValue>
                        ) : (
                          <RowBlank>{t("specsheetScan.needs.blank")}</RowBlank>
                        )}
                      </CheckRowMain>
                      {hasReading ? (
                        <Accept
                          type="button"
                          $on={agreed}
                          disabled={thrown}
                          title={t(
                            agreed
                              ? "specsheetScan.needs.accepted"
                              : "specsheetScan.needs.accept",
                          )}
                          aria-label={t(
                            agreed
                              ? "specsheetScan.needs.acceptedField"
                              : "specsheetScan.needs.acceptField",
                            { field: label(field) },
                          )}
                          aria-pressed={agreed}
                          onClick={() =>
                            setAccepted((current) => {
                              const next = new Set(current);
                              if (next.has(field)) next.delete(field);
                              else next.add(field);
                              return next;
                            })
                          }
                        >
                          <Check2 size={18} />
                        </Accept>
                      ) : null}
                      {/* Throwing a reading away leaves the sheet's own value
                          alone -- it is simply not written. Toggles, like the
                          tick: a tap that cannot be taken back is a trap. */}
                      {hasReading ? (
                        <Discard
                          type="button"
                          $on={thrown}
                          title={t(
                            thrown
                              ? "specsheetScan.needs.discarded"
                              : "specsheetScan.needs.discard",
                          )}
                          aria-label={t(
                            thrown
                              ? "specsheetScan.needs.discardedField"
                              : "specsheetScan.needs.discardField",
                            { field: label(field) },
                          )}
                          aria-pressed={thrown}
                          onClick={() =>
                            setDiscarded((current) => {
                              const next = new Set(current);
                              if (next.has(field)) next.delete(field);
                              else next.add(field);
                              return next;
                            })
                          }
                        >
                          <X size={20} />
                        </Discard>
                      ) : null}
                    </CheckRowSplit>
                  );
                })
              )}
            </Card>

            <ScanOvalCard
              values={kept}
              flagged={flagged}
              onPick={openPicker}
            />

            {!intoOpenSheet && (
              <ScanBallCard
                ball={ballEdits}
                checked={addBall}
                onCheck={setAddBall}
                onChange={setBallEdits}
              />
            )}

            {homeless.length > 0 && (
              <Card>
                <CardLabel>{t("specsheetScan.needs.other")}</CardLabel>
                {homeless.map((field) =>
                  PICKABLE_FIELDS.includes(field) ? (
                    <CheckRow
                      key={field}
                      type="button"
                      $flagged={flagged.has(field)}
                      onClick={() => openPicker(field)}
                    >
                      <RowKey>{label(field)}</RowKey>
                      {edited[field] ? (
                        <RowValue>{edited[field]}</RowValue>
                      ) : (
                        <RowBlank>{t("specsheetScan.needs.blank")}</RowBlank>
                      )}
                    </CheckRow>
                  ) : (
                    <FreeRow key={field}>
                      <FreeLabel>{label(field)}</FreeLabel>
                      <FreeInput
                        ref={(el) => {
                          inputRefs.current[field] = el;
                        }}
                        value={edited[field] || ""}
                        onFocus={() => setActiveField(field)}
                        onChange={(e) =>
                          setEdited({ ...edited, [field]: e.target.value })
                        }
                      />
                    </FreeRow>
                  ),
                )}
              </Card>
            )}

            {review.notFound && review.notFound.length > 0 && (
              <Muted>
                {t("specsheetScan.notFoundCount", {
                  count: review.notFound.length,
                })}
              </Muted>
            )}
          </SideColumn>
        </Columns>

        <Foot>
          <HeaderActionButton
            secondary
            onClick={() => navigate(backTo)}
            disabled={!!confirming}
          >
            {t("message.confirm.cancel")}
          </HeaderActionButton>
          <HeaderActionButton
            onClick={handleApply}
            disabled={keptCount === 0 || !!confirming || searching}
          >
            {confirming
              ? t("specsheetScan.applying")
              : intoOpenSheet
                ? t("specsheetScan.apply", { count: keptCount })
                : chosen
                  ? t("specsheetScan.batch.confirmExisting", {
                      name: chosenName,
                    })
                  : t("specsheetScan.batch.confirmNew")}
          </HeaderActionButton>
        </Foot>
      </Screen>
    </>
  );
};

const mapState = (state) => ({
  review: state.specsheet.batchReview,
  loadingJobId: state.specsheet.batchReviewLoading,
  confirming: state.specsheet.batchConfirming,
  applied: state.specsheet.batchApplied,
  candidates: state.specsheet.batchCandidates,
  created: state.specsheet.batchCreated,
  searching: state.specsheet.batchSearching,
  target: state.specsheet.batchTarget,
  exactId: state.specsheet.batchExactId,
  error: state.specsheet.batchError,
});

const mapDispatch = (dispatch) => ({
  onLoad: (jobId) => dispatch(specsheetBatchReview({ jobId })),
  onLeave: () => dispatch(specsheetBatchReviewClose()),
  onConfirm: (payload) => dispatch(specsheetBatchConfirm(payload)),
  onTarget: (customerId) => dispatch(specsheetBatchTarget({ customerId })),
  onApplyToSheet: (specsheetValues) =>
    dispatch(specsheetHandleForm({ specsheetValues })),
});

export const ScanReviewScreenContainer = connect(
  mapState,
  mapDispatch,
)(ScanReviewScreen);

export default ScanReviewScreenContainer;
